import { KeyCode } from "./KeyCode.js";
import { object } from "./ObjectUtil.js";

let instantiationAllowed = false;

export interface TabTrapPushOptions {
    element: HTMLElement;
    focus?: TabTrapFocus;
    focusBefore?: HTMLElement | (() => HTMLElement);
}

export type TabTrapFocus = "first" | "last" | number | HTMLElement;

export class TabTrap {

    private _element: HTMLElement | null = null;
    private _active: boolean = false;
    private _onKeyDown: (e: KeyboardEvent) => void;
    private _listening: boolean = false;
    private _elements: TabTrapPushOptions[] = [];

    constructor(element: HTMLElement | null = null, active: boolean = false) {
        this._onKeyDown = (e) => this.onKeyDown(e);
        this._active = active;
        this.setElement(element);

        if (!instantiationAllowed) {
            console.warn(`Do not instantiate e5.TabTrap use a constant e5.tabTrap instance instead.`);
        }
    }

    public push(options: TabTrapPushOptions): void {
        options = object.merge({
            focusBefore: document.activeElement as HTMLElement
        }, options);

        this._elements.push(options);

        this.setElement(options.element);

        if (options.focus) {
            this.focus(options.focus);
        } else if (document.activeElement) {
            try {
                //@ts-ignore
                document.activeElement.blur();
            } catch (error) {

            }
        }
    }

    public pop(): void {
        const last = this._elements.pop();
        const top = this._elements[this._elements.length - 1];
        this.setElement(top?.element || null);

        setTimeout(() => {
            if (typeof last?.focusBefore === "function") {
                const e = last.focusBefore();
                if (e) {
                    e.focus();
                }
            }
            else if (last?.focusBefore) {
                last.focusBefore.focus();
            }
            else if (top?.focus) {
                this.focus(top.focus);
            }
            else if (top && document.activeElement) {
                try {
                    //@ts-ignore
                    document.activeElement.blur();
                } catch (error) {

                }
            }
        });
    }

    public getElement(): HTMLElement | null {
        return this._element;
    }

    public setElement(value: HTMLElement | null): void {
        if (this._element === value) {
            return;
        }
        this._element = value;

        this.listening = this._active && this._element !== null;
    }

    /**
     * @deprecated TabTrap.element is deprecated. Use TabTrap.push() and TabTrap.pop() and TabTrap.getElement().
     */
    public get element(): HTMLElement | null {
        return this._element;
    }

    /**
     * @deprecated TabTrap.element is deprecated. Use TabTrap.push() and TabTrap.pop().
     */
    public set element(value: HTMLElement | null) {
        console.warn("TabTrap.element is deprecated. Use TabTrap.push() and TabTrap.pop().");
        this.setElement(value);
    }

    public focus(target: TabTrapFocus = "first"): void {
        if (!this._element) {
            return;
        }

        var nodes = this.getTabbableNodes();

        var index: number = NaN;
        if (target === "first") {
            index = 0;
        }
        else if (target === "last") {
            index = nodes.length - 1;
        }
        else if (typeof target === "number") {
            index = target;
        } else if (typeof target === "object") {
            index = nodes.indexOf(target);
        }

        if (!isNaN(index) && nodes[index]) {
            nodes[index].focus();
        } else if (document.activeElement) {
            try {
                //@ts-ignore
                document.activeElement.blur();
            } catch (error) {

            }
        }
    }

    private onKeyDown(e: KeyboardEvent): void {
        if (KeyCode.is(e, KeyCode.TAB)) {

            e.preventDefault();

            var nodes = this.getTabbableNodes();

            if (nodes.length === 0) {
                // console.warn("The TabTrap element has no tabbable nodes.");
                this._element?.focus();
                return;
            }

            if (!document.activeElement) {
                nodes[0].focus();
                // console.log("Focus default");
                return;
            }

            var index = nodes.indexOf(document.activeElement as any);
            if (index === -1) {
                nodes[0].focus();
                // console.log("Focus first");
                return;
            }

            if (e.shiftKey) {
                if (index === -1 || index === 0) {
                    nodes[nodes.length - 1].focus();
                } else if (index > 0) {
                    nodes[index - 1].focus();
                }
            } else {
                if (index === -1 || index === nodes.length - 1) {
                    nodes[0].focus();
                }
                else if (index < nodes.length - 1) {
                    nodes[index + 1].focus();
                }
            }
        }
    }

    private getTabbableNodes(): HTMLElement[] {

        if (!this._element) {
            return [];
        }

        //@see https://github.com/focus-trap/tabbable/blob/master/src/index.js
        const candidateSelectors = [
            'input',
            'select',
            'textarea',
            'a[href]',
            'button',
            '[tabindex]',
            'audio[controls]',
            'video[controls]',
            '[contenteditable]:not([contenteditable="false"])',
            'details>summary:first-of-type',
            'details',
        ];

        var nodes: HTMLElement[] = Array.prototype.slice.apply(this._element.querySelectorAll(candidateSelectors.join(",")));

        //Filter by "focusable".
        var result: HTMLElement[] = [];
        for (var node of nodes) {

            if (this.isNodeMatchingSelectorFocusable(node)) {
                result.push(node);
            }
        }
        return result;
    }

    private isNodeMatchingSelectorFocusable(node: Element) {
        if (
            !node
            || node.hasAttribute("disabled")
            || this.isHiddenInput(node)
            || this.isHidden(node)
            || this.isTabDisabled(node as any)
            || this.isInert(node)
            /* For a details element with a summary, the summary element gets the focused  */
            // isDetailsWithSummary(node)
        ) {
            return false;
        }
        return true;
    }

    private isInert(node: Element) {
        if (node.hasAttribute('inert')) {
            return true;
        }

        var n: Element | null = node;
        while (n) {
            if (n.hasAttribute('inert')) {
                return true;
            }
            n = n.parentElement;
        }

        return false;
    }

    private isHidden(node: Element) {
        if (getComputedStyle(node).visibility === 'hidden') {
            return true;
        }

        var n: Element | null = node;
        while (n) {
            if (getComputedStyle(n).display === 'none') {
                return true;
            }
            n = n.parentElement;
        }

        return false;
    }

    private isTabDisabled(node: HTMLOrSVGElement): boolean {
        if (node.tabIndex === undefined) {
            return false;
        }
        return node.tabIndex < 0;
    }

    private isInput(node: Element): node is HTMLInputElement {
        return node.tagName === 'INPUT';
    }

    private isHiddenInput(node: Element): node is HTMLInputElement {
        return this.isInput(node) && node.type === 'hidden';
    }

    /**
     * @deprecated Use e5.TabTrap.element instead.
     */
    public get active(): boolean {
        return this._active;
    }

    private _triggeredActive: boolean = false;

    /**
     * @deprecated Use e5.TabTrap.element instead.
     */
    public set active(value: boolean) {
        if (!this._triggeredActive) {
            this._triggeredActive = true;
            console.warn(`Do not use e5.TabTrap.active use e5.TabTrap.element instead.`);
        }

        if (this._active == value) {
            return;
        }
        this._active = value;

        this.listening = this._active && this._element !== null;
    }

    private get listening(): boolean {
        return this._listening;
    }

    private set listening(value: boolean) {
        if (this._listening == value) {
            return;
        }
        this._listening = value;

        if (value) {
            document.addEventListener("keydown", this._onKeyDown, true);
        } else {
            document.removeEventListener("keydown", this._onKeyDown, true);
        }
    }
}

instantiationAllowed = true;
export const tabTrap = new TabTrap(null, true);
instantiationAllowed = false;